import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { buildStayMediaPath, validateStayMediaDraft, type StayMediaDraft, type StayMediaItem, type StayMediaKind } from "./stayMediaModel";

type MediaRow = {
  id: string;
  stay_reservation_id: string;
  pet_id: string;
  log_date: string;
  logged_at: string;
  description: string;
  storage_path: string;
  media_kind: StayMediaKind;
  mime_type: string;
  file_size: number;
};

type MediaTable = {
  select: (columns: string) => MediaTable;
  eq: (column: string, value: string) => MediaTable;
  order: (column: string, options: { ascending: boolean }) => Promise<{ data: MediaRow[] | null; error: Error | null }>;
  insert: (row: Record<string, unknown>) => Promise<{ error: Error | null }>;
  delete: () => { eq: (column: string, value: string) => Promise<{ error: Error | null }> };
};

const mediaTable = () => (supabase as unknown as { from: (table: "stay_reservation_media") => MediaTable }).from("stay_reservation_media");
const mediaStorage = () => supabase.storage.from("stay-media");

export const stayMediaKey = (reservationId: string | null, day: string) => ["stay-media", reservationId, day];

async function signedUrl(storagePath: string) {
  const { data, error } = await mediaStorage().createSignedUrl(storagePath, 60 * 15);
  if (error || !data?.signedUrl) throw error ?? new Error("stay_media_signed_url_failed");
  return data.signedUrl;
}

/** O select devolve uma coluna de data e outra de hora; a ordem certa é a das duas juntas. */
export function sortStayMediaRows<T extends { log_date: string; logged_at: string }>(rows: T[]): T[] {
  return [...rows].sort((left, right) =>
    `${left.log_date} ${left.logged_at}`.localeCompare(`${right.log_date} ${right.logged_at}`),
  );
}

const MEDIA_COLUMNS = "id, stay_reservation_id, pet_id, log_date, logged_at, description, storage_path, media_kind, mime_type, file_size";

const toMediaItem = async (row: MediaRow): Promise<StayMediaItem> => ({
  id: row.id,
  reservationId: row.stay_reservation_id,
  petId: row.pet_id,
  day: row.log_date,
  time: row.logged_at.slice(0, 5),
  description: row.description,
  storagePath: row.storage_path,
  mediaKind: row.media_kind,
  mimeType: row.mime_type,
  fileSize: row.file_size,
  signedUrl: await signedUrl(row.storage_path),
});

/** Toda a mídia da reserva, para o resumo de checkout. */
export async function fetchStayReservationMedia(reservationId: string): Promise<StayMediaItem[]> {
  const { data, error } = await mediaTable()
    .select(MEDIA_COLUMNS)
    .eq("stay_reservation_id", reservationId)
    .order("logged_at", { ascending: true });
  if (error) throw error;
  return Promise.all(sortStayMediaRows(data ?? []).map(toMediaItem));
}

export async function uploadStayMediaObject(input: StayMediaDraft & {
  reservationId: string;
  petId: string;
  day: string;
  time: string;
  id?: string;
  insert: (row: Record<string, unknown>) => Promise<{ error: Error | null }>;
}) {
  const validation = validateStayMediaDraft(input);
  if (validation.ok === false) throw new Error(validation.error);
  const id = input.id ?? crypto.randomUUID();
  const storagePath = buildStayMediaPath(input.reservationId, input.day, input.file.name, id);
  const { error: uploadError } = await mediaStorage().upload(storagePath, input.file, {
    contentType: input.file.type,
    upsert: false,
  });
  if (uploadError) throw uploadError;

  const { error: insertError } = await input.insert({
    stay_reservation_id: input.reservationId,
    pet_id: input.petId,
    log_date: input.day,
    logged_at: input.time,
    description: input.description.trim(),
    storage_path: storagePath,
    media_kind: validation.kind,
    mime_type: input.file.type,
    file_size: input.file.size,
  });
  if (insertError) {
    await mediaStorage().remove([storagePath]);
    throw insertError;
  }
  return storagePath;
}

export async function deleteStayMediaObject(input: {
  storagePath: string;
  id: string;
  remove: (paths: string[]) => Promise<{ error: Error | null }>;
  deleteRow: () => Promise<{ error: Error | null }>;
}) {
  const { error: removeError } = await input.remove([input.storagePath]);
  if (removeError) throw removeError;
  const { error: deleteError } = await input.deleteRow();
  if (deleteError) throw deleteError;
}

export function useStayMedia(reservationId: string | null, day: string) {
  return useQuery({
    queryKey: stayMediaKey(reservationId, day),
    enabled: Boolean(reservationId && day),
    queryFn: async (): Promise<StayMediaItem[]> => {
      if (!reservationId) return [];
      const { data, error } = await mediaTable()
        .select(MEDIA_COLUMNS)
        .eq("stay_reservation_id", reservationId)
        .eq("log_date", day)
        .order("logged_at", { ascending: true });
      if (error) throw error;
      return Promise.all((data ?? []).map(toMediaItem));
    },
  });
}

export function useUploadStayMedia() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  return useMutation({
    mutationFn: async (input: StayMediaDraft & { reservationId: string; petId: string; day: string; time: string }) => {
      const table = mediaTable();
      return uploadStayMediaObject({
        ...input,
        insert: async (row) => {
          const result = await table.insert({ ...row, created_by: profile?.id ?? null });
          return { error: result.error };
        },
      });
    },
    onSuccess: (_data, input) => queryClient.invalidateQueries({ queryKey: stayMediaKey(input.reservationId, input.day) }),
  });
}

export function useDeleteStayMedia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; storagePath: string; reservationId: string; day: string }) => {
      const table = mediaTable();
      await deleteStayMediaObject({
        storagePath: input.storagePath,
        id: input.id,
        remove: (paths) => mediaStorage().remove(paths),
        deleteRow: async () => {
          return table.delete().eq("id", input.id);
        },
      });
    },
    onSuccess: (_data, input) => queryClient.invalidateQueries({ queryKey: stayMediaKey(input.reservationId, input.day) }),
  });
}
